import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  chatNotificationsApi,
  type ChatNotificationItem,
} from "@/api/chat-notifications";
import { useTenant } from "@/context/tenant-context";
import { useNotificationPrefs } from "@/hooks/use-notification-prefs";

const POLL_MS = 8000;
const SEEN_STORAGE_KEY_PREFIX = "proclaw-chat-notif-seen:";

/** Pull the set of messageIds we've already toasted for this user+tenant. */
function readSeen(storageKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeSeen(storageKey: string, seen: Set<string>) {
  try {
    // Cap to last 500 ids to avoid unbounded growth.
    const arr = Array.from(seen).slice(-500);
    localStorage.setItem(storageKey, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

/**
 * Returns what the user is currently looking at, parsed from the workspace
 * URL query string:
 *
 *   sessionId — the open chat session (workspaces use `?sessionId=<uuid>`).
 *   agentId   — the agent whose chat is on screen. Only reported when the
 *               chat surface is the active mode; in org/workflows/tasks views
 *               the agentId can linger in the URL without a chat being shown.
 *
 * Both are used to suppress toasts the user doesn't need: there's no point
 * popping an alert for the conversation (or the agent) they're already
 * watching.
 */
function useActiveThread(): { sessionId: string | null; agentId: string | null } {
  const location = useLocation();
  return useMemo(() => {
    const q = new URLSearchParams(location.search);
    const mode = q.get("mode");
    const onChatSurface = mode === null || mode === "chat" || mode === "code";
    return {
      sessionId: q.get("sessionId"),
      agentId: onChatSurface ? q.get("agentId") : null,
    };
  }, [location.search]);
}

export interface ChatNotifications {
  items: ChatNotificationItem[];
  unreadCount: number;
  isLoading: boolean;
}

/**
 * Polls for unread agent chat messages in the tenant, fires sonner toasts
 * for new ones (skipping anything already seen, and the session the user is
 * currently viewing). Exposes the full unread list for a bell/popover UI.
 */
export function useChatNotifications(): ChatNotifications {
  const { tenantId } = useTenant();
  const { sessionId: activeSessionId, agentId: activeAgentId } = useActiveThread();
  const { data: prefs } = useNotificationPrefs();
  // Treat anything other than an explicit `false` (including the loading
  // window) as enabled so we don't drop a real toast while prefs load.
  const chatToastsEnabled = prefs?.chatToastsEnabled !== false;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seenRef = useRef<Set<string> | null>(null);
  const firstPollRef = useRef(true);

  const storageKey = `${SEEN_STORAGE_KEY_PREFIX}${tenantId}`;
  if (seenRef.current === null) {
    seenRef.current = readSeen(storageKey);
  }

  const { data, isLoading } = useQuery({
    queryKey: ["chat-notifications", tenantId],
    queryFn: () => chatNotificationsApi.listUnread(tenantId, 50),
    enabled: !!tenantId,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const items = data?.items ?? [];

  useEffect(() => {
    if (!seenRef.current) return;
    const seen = seenRef.current;
    const fresh = items.filter(
      (m) =>
        !seen.has(m.messageId) &&
        // Not the conversation the user is currently reading…
        m.sessionId !== activeSessionId &&
        // …and not the agent whose chat is on screen.
        (activeAgentId === null || m.agentId !== activeAgentId),
    );

    if (fresh.length === 0) return;

    // Toasts opted out: still record these as seen so re-enabling later
    // doesn't replay a backlog, and consume the first-poll flag so we don't
    // fire the aggregate catch-up toast on the next tick.
    if (!chatToastsEnabled) {
      firstPollRef.current = false;
      for (const m of fresh) seen.add(m.messageId);
      writeSeen(storageKey, seen);
      return;
    }

    // First poll after mount: suppress toasts (catch-up state). Still count
    // them as seen on subsequent polls so we don't spam on the next tick if
    // the page was backgrounded. But show a single aggregate toast so the
    // user knows there's unread waiting for them.
    if (firstPollRef.current) {
      firstPollRef.current = false;
      if (fresh.length > 0) {
        toast.message(
          fresh.length === 1
            ? `${fresh[0].agentName} posted while you were away`
            : `${fresh.length} new agent messages while you were away`,
          { description: "Open the notifications menu to catch up." },
        );
      }
    } else {
      for (const m of fresh.slice(0, 3)) {
        const title = m.directToMe
          ? `${m.agentName} replied to you`
          : `${m.agentName} posted in ${m.channelName ?? m.sessionTitle}`;
        const description = m.content.length > 140
          ? m.content.slice(0, 140) + "…"
          : m.content;
        const openTarget = m.agentId
          ? (() => {
              const params = new URLSearchParams({
                agentId: m.agentId,
                sessionId: m.sessionId,
              });
              if (m.channelId) params.set("channelId", m.channelId);
              return `/tenants/${tenantId}/workspace?${params.toString()}`;
            })()
          : null;
        const showToast = m.directToMe ? toast.success : toast.message;
        showToast(title, {
          description,
          duration: m.directToMe ? 8000 : 5000,
          action: openTarget
            ? {
                label: "Open",
                onClick: () => navigate(openTarget),
              }
            : undefined,
        });
      }
      if (fresh.length > 3) {
        toast.message(`+${fresh.length - 3} more new messages`, {
          description: "Open the bell to see all unread.",
        });
      }
    }

    for (const m of fresh) seen.add(m.messageId);
    writeSeen(storageKey, seen);
  }, [items, activeSessionId, activeAgentId, chatToastsEnabled, navigate, storageKey, tenantId]);

  const markRead = async (sessionId: string) => {
    try {
      await chatNotificationsApi.markSessionRead(sessionId);
      queryClient.invalidateQueries({ queryKey: ["chat-notifications", tenantId] });
    } catch {
      /* non-fatal — next poll will reconcile */
    }
  };

  // Expose markRead on window-scoped ref so components that don't go through
  // this hook (chat-surface) can call it via a simple helper.
  useEffect(() => {
    (window as unknown as { __proclawMarkSessionRead?: typeof markRead }).__proclawMarkSessionRead = markRead;
    return () => {
      (window as unknown as { __proclawMarkSessionRead?: typeof markRead }).__proclawMarkSessionRead = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Bell badge and visible list should both ignore the session the user is
  // currently viewing — those messages aren't "unread" in any meaningful
  // sense. (Server-side `markSessionRead` only fires on session-switch, so
  // the API still returns them until the next navigation.)
  const visibleItems = useMemo(
    () => items.filter((m) => m.sessionId !== activeSessionId),
    [items, activeSessionId],
  );

  return {
    items: visibleItems,
    unreadCount: visibleItems.length,
    isLoading,
  };
}

/** Helper for code paths without a hook — calls the markRead bound by the
 * active useChatNotifications() (in TenantShell). Safe to call anywhere; a
 * no-op if the hook isn't mounted. */
export function markSessionReadAndRefresh(sessionId: string) {
  const fn = (window as unknown as {
    __proclawMarkSessionRead?: (sessionId: string) => Promise<void>;
  }).__proclawMarkSessionRead;
  if (fn) void fn(sessionId);
}
